import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Calendar as CalendarIcon,
  ListChecks,
  Trash2,
  Send,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import useBillingSubscription from "@/lib/useBillingSubscription";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import { StatusBadge } from "@/components/Surface";

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function daysGrid(monthDate) {
  const start = startOfMonth(monthDate);
  const startWeekday = start.getDay(); // 0 = Sun
  const lead = startWeekday;
  const cells = [];
  for (let i = 0; i < lead; i++) {
    cells.push(null);
  }
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  for (let day = 1; day <= end; day++) {
    cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
function sameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function DraggablePost({ post, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: post.post_id });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${isDragging ? 1.02 : 1})` : undefined,
    boxShadow: isDragging ? "0 18px 40px rgba(0,0,0,0.55)" : undefined,
    zIndex: isDragging ? 50 : 1,
  };
  const colorMap = {
    draft: "border-[color:rgba(127,138,163,0.45)] bg-[color:rgba(127,138,163,0.10)] text-[color:var(--text-2)]",
    scheduled: "border-[color:rgba(77,183,255,0.45)] bg-[color:rgba(77,183,255,0.10)] text-[color:var(--info)]",
    published: "border-[color:rgba(47,231,161,0.45)] bg-[color:rgba(47,231,161,0.10)] text-[color:var(--success)]",
    failed: "border-[color:rgba(255,92,108,0.45)] bg-[color:rgba(255,92,108,0.10)] text-[color:var(--danger)]",
    pending_approval: "border-[color:rgba(255,138,61,0.45)] bg-[color:rgba(255,138,61,0.10)] text-[color:var(--accent-warm)]",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid="calendar-post-pill"
      className={"mt-1 flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] border " + (colorMap[post.status] || colorMap.draft) + " select-none cursor-grab"}
    >
      {post.image_url ? (
        <img src={post.image_url} alt="" className="h-5 w-5 rounded object-cover border border-[color:var(--border-subtle)] shrink-0" data-testid="calendar-post-thumb" />
      ) : null}
      <span className="truncate flex-1" {...attributes} {...listeners}>{post.caption}</span>
      <button onClick={() => onDelete(post)} aria-label="Delete post" className="opacity-70 hover:opacity-100" data-testid="calendar-post-delete">
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function DayCell({ date, posts, isToday, onDelete }) {
  const id = date ? `cell-${date.toISOString().slice(0, 10)}` : "empty";
  const { setNodeRef, isOver } = useDroppable({ id, disabled: !date });
  if (!date) {
    return <div className="rounded-md min-h-[96px] bg-transparent" />;
  }
  return (
    <div
      ref={setNodeRef}
      data-testid={`calendar-cell-${date.toISOString().slice(0, 10)}`}
      className={
        "relative rounded-md border bg-[color:var(--surface-1)] p-2 min-h-[96px] transition-colors " +
        (isOver
          ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.06)]"
          : "border-[color:var(--border-subtle)] hover:border-[color:var(--border-strong)]") +
        (isToday ? " ring-1 ring-[color:rgba(0,230,214,0.35)]" : "")
      }
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-[color:var(--text-2)]">{date.getDate()}</div>
        {isToday ? <span className="text-[10px] text-[color:var(--brand-primary)]">today</span> : null}
      </div>
      <div className="space-y-1">
        {posts.map((p) => (
          <DraggablePost key={p.post_id} post={p} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

// ---- Week view removed (Phase UX-Cleanup) ----
// The Content Calendar previously offered Week / Month / Agenda tabs. Week
// added visual clutter with a low-density time-slot grid that duplicated
// what Agenda already shows more compactly. We removed the tab + the
// entire WeekView / WeekSlot components. Month + Agenda remain — Month is
// the default. Drag-and-drop rescheduling still works in Month view (see
// ``handleDragEnd`` below, which only handles ``cell-*`` cell IDs now).

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [posts, setPosts] = useState([]);
  // ``postingTimes`` state removed with Week view. Month view uses the
  // post's own scheduled_at directly and doesn't need Autopilot slots.
  // Per-post in-flight publish tracker — same pattern as ContentManager.
  // Backing the Publish button with immediate local state (before the
  // network round-trip returns) is what stops double-click duplicate
  // publishes from the Calendar agenda. Backend also enforces a per-post
  // publish lock as defense-in-depth.
  const [publishingIds, setPublishingIds] = useState(() => new Set());
  const { sub: billingSub } = useBillingSubscription();
  const cells = useMemo(() => daysGrid(currentMonth), [currentMonth]);
  const today = useMemo(() => new Date(), []);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const load = async () => {
    try {
      const res = await api.get("/posts");
      setPosts(res.data);
    } catch (_e) {
      toast.error("Could not load posts");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const postsByDay = useMemo(() => {
    const map = {};
    for (const p of posts) {
      const dateKey = p.scheduled_at ? new Date(p.scheduled_at).toISOString().slice(0, 10) : (p.published_at ? new Date(p.published_at).toISOString().slice(0, 10) : null);
      if (!dateKey) continue;
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(p);
    }
    return map;
  }, [posts]);

  const unscheduled = useMemo(() => posts.filter((p) => p.status === "draft" && !p.scheduled_at), [posts]);

  const handleDragEnd = async (e) => {
    const { active, over } = e;
    if (!over) return;
    const cellId = over.id;
    const post = posts.find((p) => p.post_id === active.id);
    if (!post) return;

    let iso = null;
    if (cellId?.startsWith("cell-")) {
      // Month view: "cell-YYYY-MM-DD"
      const newDate = cellId.replace("cell-", "");
      // preserve time-of-day from scheduled_at if any, otherwise default 10:00 local
      let hh = 10, mm = 0;
      if (post.scheduled_at) {
        const dt = new Date(post.scheduled_at);
        hh = dt.getHours();
        mm = dt.getMinutes();
      }
      const local = new Date(`${newDate}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`);
      iso = local.toISOString();
    } else {
      return;
    }

    try {
      await api.patch(`/posts/${post.post_id}`, { scheduled_at: iso, status: "scheduled" });
      toast.success("Post rescheduled");
      load();
    } catch (_err) {
      toast.error("Reschedule failed");
    }
  };

  const onDelete = async (post) => {
    try {
      await api.delete(`/posts/${post.post_id}`);
      toast.success("Post deleted");
      load();
    } catch (_e) {
      toast.error("Delete failed");
    }
  };

  const onPublishNow = async (post) => {
    if (publishingIds.has(post.post_id)) return;
    // Multi-platform quota pre-check (frontend convenience only —
    // backend still enforces via 402).
    const targets = Array.isArray(post.target_platforms) && post.target_platforms.length > 0
      ? post.target_platforms
      : [post.platform];
    const unitsNeeded = Math.max(1, targets.length);
    const monthlyRemaining = billingSub?.monthly_remaining;
    if (monthlyRemaining != null && unitsNeeded > monthlyRemaining) {
      toast.error(
        `You have ${monthlyRemaining} post${monthlyRemaining === 1 ? "" : "s"} remaining this month. Publishing to ${targets.join(" + ")} would use ${unitsNeeded} posts. Remove one platform or upgrade to continue.`,
        { id: `publish-${post.post_id}`, duration: 8000 },
      );
      return;
    }
    setPublishingIds((prev) => {
      const next = new Set(prev);
      next.add(post.post_id);
      return next;
    });
    const toastId = `publish-${post.post_id}`;
    toast.loading("Publishing started. This may take a few seconds…", { id: toastId });
    try {
      const res = await api.post(`/posts/${post.post_id}/publish`);
      const data = res?.data;
      if (data?.status === "blocked_safety") {
        toast.error(
          data.error_message ||
            "This content can't be published because it may violate Postmoro's content safety rules. Please revise and try again.",
          { id: toastId, duration: 8000 },
        );
      } else if (data?.status === "blocked_org_policy") {
        // Phase 20 — Compliance review required. Surface the persisted
        // violations so the user can see which rule to revise.
        const violations = Array.isArray(data.org_compliance_violations)
          ? data.org_compliance_violations
          : [];
        const summary = violations.length
          ? violations
              .slice(0, 3)
              .map((v) => v.message || v.rule || "policy violation")
              .join(" • ")
          : (data.error_message ||
              "This post was held because it may not match your organization's content policy.");
        toast.error(summary, { id: toastId, duration: 10000 });
      } else if (data?.blocked_by === "org_compliance_unavailable") {
        toast.error(
          data.error_message ||
            "We couldn't complete the required organization content policy check right now. Nothing was published. Please try again shortly.",
          { id: toastId },
        );
      } else if (data?.status === "failed") {
        toast.error(data.error_message || "Publish couldn't complete. Open the post to see details.", { id: toastId });
      } else if (data?.status === "partial_published") {
        toast.warning(data.error_message || "Partially published — some platforms failed.", { id: toastId });
      } else if (data?.status === "published") {
        toast.success("Published", { id: toastId });
      } else {
        // Unknown / non-terminal status — never claim a false success.
        toast.info(
          data?.error_message || "Publish is still processing. Open the post to see its status.",
          { id: toastId },
        );
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isInFlight = err?.response?.status === 409 || detail?.error === "publish_in_progress";
      if (isInFlight) {
        toast.info(detail?.message || "This post is already publishing. Please wait for it to finish.", { id: toastId });
      } else {
        toast.error(
          (typeof detail === "string" ? detail : detail?.message) ||
            "Publish failed",
          { id: toastId },
        );
      }
    } finally {
      setPublishingIds((prev) => {
        const next = new Set(prev);
        next.delete(post.post_id);
        return next;
      });
    }
  };

  const onApprove = async (post) => {
    try {
      await api.post(`/posts/${post.post_id}/approve`);
      toast.success("Approved and scheduled");
      load();
    } catch (_e) {
      toast.error("Approve failed");
    }
  };

  const onReject = async (post) => {
    try {
      await api.post(`/posts/${post.post_id}/reject`);
      toast.success("Rejected");
      load();
    } catch (_e) {
      toast.error("Reject failed");
    }
  };

  const monthLabel = currentMonth.toLocaleString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
      <PageHeader
        title="Content Calendar"
        subtitle="Drag any post to reschedule it. Auto-publishing kicks in when the time arrives."
        testidPrefix="calendar"
        action={
          <Link to="/generate">
            <Button className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold" data-testid="calendar-quick-generate">
              <Sparkles className="h-4 w-4 mr-1.5" /> New post
            </Button>
          </Link>
        }
      />

      <Tabs defaultValue="month" className="w-full">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <TabsList className="bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)]">
            <TabsTrigger value="month" data-testid="calendar-tab-month"><CalendarIcon className="h-4 w-4 mr-1" /> Month</TabsTrigger>
            <TabsTrigger value="agenda" data-testid="calendar-tab-agenda"><ListChecks className="h-4 w-4 mr-1" /> Agenda</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth((d) => addMonths(d, -1))} data-testid="calendar-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="font-display text-base font-semibold min-w-[140px] text-center" data-testid="calendar-month-label">{monthLabel}</div>
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth((d) => addMonths(d, 1))} data-testid="calendar-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <TabsContent value="month">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <GlassCard className="p-4">
              <div className="grid grid-cols-7 gap-2 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d} className="text-xs uppercase tracking-wider text-[color:var(--text-3)] text-center">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2" data-testid="calendar-grid">
                {cells.map((d, i) => {
                  const key = d ? d.toISOString().slice(0, 10) : `e-${i}`;
                  return (
                    <DayCell
                      key={key}
                      date={d}
                      isToday={d && sameDay(d, today)}
                      posts={d ? postsByDay[key] || [] : []}
                      onDelete={onDelete}
                    />
                  );
                })}
              </div>
            </GlassCard>

            {/* Unscheduled drafts */}
            <GlassCard className="p-5 mt-4" data-testid="calendar-unscheduled">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-base font-semibold">Unscheduled drafts</div>
                <span className="text-xs text-[color:var(--text-3)]">{unscheduled.length} draft(s)</span>
              </div>
              {unscheduled.length === 0 ? (
                <div className="text-sm text-[color:var(--text-3)]">No drafts. <Link to="/generate" className="text-[color:var(--brand-primary)] hover:underline">Generate one</Link>.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {unscheduled.map((p) => (
                    <DraggablePost key={p.post_id} post={p} onDelete={onDelete} />
                  ))}
                </div>
              )}
            </GlassCard>
          </DndContext>
        </TabsContent>

        <TabsContent value="agenda">
          <GlassCard className="p-4" data-testid="calendar-agenda-view">
            {posts.length === 0 ? (
              <div className="py-10 text-center text-sm text-[color:var(--text-3)]" data-testid="empty-calendar">
                No posts yet. <Link to="/generate" className="text-[color:var(--brand-primary)] hover:underline">Generate one</Link> and schedule it.
              </div>
            ) : (
              <div className="space-y-2">
                {posts.slice().sort((a, b) => {
                  const ax = a.scheduled_at || a.created_at;
                  const bx = b.scheduled_at || b.created_at;
                  return new Date(bx) - new Date(ax);
                }).map((p) => (
                  <motion.div
                    key={p.post_id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 p-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
                    data-testid="agenda-row"
                  >
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt="post"
                        className="h-14 w-14 rounded-md object-cover border border-[color:var(--border-subtle)]"
                        data-testid="agenda-post-image"
                      />
                    ) : (
                      <div className="h-9 w-9 rounded-md bg-[color:var(--surface-3)] flex items-center justify-center text-[color:var(--brand-primary)]">
                        <CalendarIcon className="h-4 w-4" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[color:var(--text-1)] line-clamp-2 whitespace-pre-line">{p.caption}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-[color:var(--text-3)] flex-wrap">
                        <StatusBadge status={p.status} />
                        <span>{p.platform}</span>
                        {p.source === "autopilot" ? (
                          <span className="text-[10px] text-[color:var(--brand-secondary)]">· Autopilot</span>
                        ) : null}
                        {p.scheduled_at ? <span>· scheduled {new Date(p.scheduled_at).toLocaleString()}</span> : null}
                        {p.published_at ? <span>· published {new Date(p.published_at).toLocaleString()}</span> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.status === "pending_approval" ? (
                        <>
                          <Button size="sm" className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]" onClick={() => onApprove(p)} data-testid="agenda-approve">
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)]" onClick={() => onReject(p)} data-testid="agenda-reject">
                            Reject
                          </Button>
                        </>
                      ) : (
                        <>
                          {p.status !== "published" ? (() => {
                            const isPub = publishingIds.has(p.post_id);
                            return (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-1)] disabled:opacity-60 disabled:cursor-not-allowed"
                                onClick={() => { if (!isPub) onPublishNow(p); }}
                                disabled={isPub}
                                title={isPub ? "This post is already publishing. Please wait for it to finish." : undefined}
                                data-testid="agenda-publish-now"
                              >
                                {isPub ? (
                                  <>
                                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Publishing…
                                  </>
                                ) : (
                                  <>
                                    <Send className="h-3.5 w-3.5 mr-1" /> Publish now
                                  </>
                                )}
                              </Button>
                            );
                          })() : null}
                          <Button size="sm" variant="ghost" className="text-[color:var(--danger)] hover:text-[color:var(--danger)]" onClick={() => onDelete(p)} data-testid="agenda-delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </GlassCard>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
